import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '../services/api';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [synced, setSynced] = useState(false);
  const [cartError, setCartError] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedDeliverySlot, setSelectedDeliverySlotState] = useState(() => {
    const stored = localStorage.getItem('selectedDeliverySlot');
    return stored ? JSON.parse(stored) : null;
  });
  const [zoneCheckResult, setZoneCheckResult] = useState(null);

  const toggleCart = () => setIsCartOpen(prev => !prev);
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  useEffect(() => {
    if (user) {
      fetchCartFromAPI();
    } else {
      const stored = localStorage.getItem('cart');
      setCartItems(stored ? JSON.parse(stored) : []);
      setSynced(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!synced || !user) {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems, synced, user]);

  const fetchCartFromAPI = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const items = await cartApi.get();
      setCartItems(items || []);
      setCartError(null);
    } catch (err) {
      console.error('Failed to fetch cart:', err);
      const stored = localStorage.getItem('cart');
      setCartItems(stored ? JSON.parse(stored) : []);
    } finally {
      setLoading(false);
      setSynced(true);
    }
  };

  const clearCartError = () => setCartError(null);

  const addToLocalCart = (cart, product, quantity) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      return cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: (item.quantity || 1) + quantity }
          : item
      );
    }
    return [...cart, { ...product, quantity }];
  };

  const addToCart = async (product, quantity = 1) => {
    if (loading) return;
    setLoading(true);
    const productId = product.id;
    const baseId = productId || product.name?.replace(/\s+/g, '-').toLowerCase();
    const variantId = product.shade || product.size
      ? `${baseId}-${product.shade || ''}-${product.size || ''}`.replace(/\s+/g, '-').toLowerCase()
      : baseId;

    const productWithId = {
      ...product,
      id: variantId,
      product_id: productId,
      productId: productId,
      variant_id: product.variant_id || null,
      sku: product.sku || null,
      finalPrice: product.finalPrice || product.price,
      unit_price: product.finalPrice || product.price,
      stock_quantity: product.variant_stock_quantity || product.stock_quantity || product.total_stock || 0
    };

    // FIX: Check cart quantity before adding
    const existingItem = cartItems.find(item =>
      item.product_id === productId &&
      (item.variant_id === productWithId.variant_id ||
        (!item.variant_id && !productWithId.variant_id))
    );
    const currentCartQty = existingItem?.quantity || 0;
    const requestedTotal = currentCartQty + quantity;

    // Determine available stock based on product type
    const availableStock = productWithId.variant_id
      ? product.total_stock || 0
      : (product.stock_quantity || product.total_stock || 0);

    if (availableStock && requestedTotal > availableStock) {
      const msg = `Only ${availableStock} units available. You already have ${currentCartQty} in your cart.`;
      setCartError(msg);
      toast.warning(msg);
      return;
    }

    if (user) {
      try {
        await cartApi.addItem({
          productId: productId,
          quantity,
          variant_id: productWithId.variant_id,
          sku: productWithId.sku,
          unit_price: productWithId.finalPrice,
          base_price: productWithId.base_price || productWithId.finalPrice,
          color: productWithId.color || null,
          size: productWithId.size || null
        });
        await fetchCartFromAPI();
        setCartError(null);
      } catch (err) {
        const errorMsg = err.message || 'Failed to add to cart';
        if (err.code === 'INSUFFICIENT_STOCK' || err.response?.status === 409) {
          setCartError(errorMsg);
          throw err;
        }
        console.warn('Cart API failed, using local fallback:', err);
        const stored = localStorage.getItem('cart');
        const localCart = stored ? JSON.parse(stored) : [];
        const updated = addToLocalCart(localCart, productWithId, quantity);
        setCartItems(updated);
        localStorage.setItem('cart', JSON.stringify(updated));
      } finally {
        setLoading(false);
      }
    } else {
      setCartItems(prev => {
        const updated = addToLocalCart(prev, productWithId, quantity);
        localStorage.setItem('cart', JSON.stringify(updated));
        return updated;
      });
      setLoading(false);
    }
  };

  const removeFromCart = async (id) => {
    if (user) {
      setLoading(true);
      try {
        const item = cartItems.find(i => i.id === id || i.product_id === id || i.productId === id);
        const productId = item?.product_id || item?.productId || parseInt(id) || id;
        await cartApi.removeItem(productId);
        await fetchCartFromAPI();
      } catch (err) {
        console.error('API remove failed, using local:', err);
        setCartItems(prev => prev.filter(item => item.id !== id && item.product_id !== id && item.productId !== id));
      } finally {
        setLoading(false);
      }
    } else {
      setCartItems(prev => {
        const updated = prev.filter(item => item.id !== id && item.product_id !== id && item.productId !== id);
        localStorage.setItem('cart', JSON.stringify(updated));
        return updated;
      });
    }
  };

  const updateQuantity = async (id, quantity) => {
    if (quantity < 1) return;

    if (user) {
      setLoading(true);
      try {
        const item = cartItems.find(i =>
          i.id === id || i.cart_item_id === id
        );
        const cartItemId = item?.id
          || item?.cart_item_id || id;
        await cartApi.updateItem(cartItemId, quantity);
        await fetchCartFromAPI();
      } catch (err) {
        console.error('Update quantity failed:', err);
        if (err.response?.status === 409 || err.code === 'INSUFFICIENT_STOCK') {
          toast.error('Not enough stock available');
        }
        await fetchCartFromAPI();
      } finally {
        setLoading(false);
      }
    } else {
      setCartItems(prev => {
        const item = prev.find(i => i.id === id);
        if (item) {
          const availableStock = item.stock_quantity || item.total_stock || 0;
          if (availableStock && quantity > availableStock) {
            return prev; // Don't update — at limit
          }
        }
        const updated = prev.map(item => item.id === id ? { ...item, quantity } : item);
        localStorage.setItem('cart', JSON.stringify(updated));
        return updated;
      });
    }
  };

  const clearCart = async () => {
    if (user) {
      try {
        await cartApi.clear();
      } catch (err) {
        console.error('Failed to clear cart:', err);
      }
    }
    setCartItems([]);
    localStorage.setItem('cart', JSON.stringify([]));
  };

  const setSelectedDeliverySlot = useCallback((slot) => {
    setSelectedDeliverySlotState(slot);
    localStorage.setItem('selectedDeliverySlot', JSON.stringify(slot));
  }, []);

  const clearSelectedDeliverySlot = useCallback(() => {
    setSelectedDeliverySlotState(null);
    localStorage.removeItem('selectedDeliverySlot');
  }, []);

  const clearZoneCheckResult = useCallback(() => setZoneCheckResult(null), []);

  const setGiftCardPurchase = (giftCardItem) => {
    localStorage.setItem('giftCardPurchase', JSON.stringify(giftCardItem));
  };

  const getGiftCardPurchase = () => {
    const stored = localStorage.getItem('giftCardPurchase');
    return stored ? JSON.parse(stored) : null;
  };

  const clearGiftCardPurchase = () => {
    localStorage.removeItem('giftCardPurchase');
  };

  const cartTotal = cartItems.reduce((sum, item) => {
    const itemPrice = item.unit_price || item.finalPrice || item.price;
    return sum + (itemPrice * item.quantity);
  }, 0);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      cartCount,
      cartTotal,
      loading,
      cartError,
      isCartOpen,
      toggleCart,
      openCart,
      closeCart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      refreshCart: fetchCartFromAPI,
      selectedDeliverySlot,
      setSelectedDeliverySlot,
      clearSelectedDeliverySlot,
      zoneCheckResult,
      setZoneCheckResult,
      clearZoneCheckResult,
      setGiftCardPurchase,
      getGiftCardPurchase,
      clearGiftCardPurchase,
      clearCartError
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
